import React, {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";

const CartContext = createContext(null);

const storageKey = (shopId) => `shopCart_${shopId}`;

const lineKey = (productId, productVariantId) =>
  `${productId}::${productVariantId || ""}`;

function loadCart(shopId) {
  if (!shopId) return [];
  try {
    const raw = localStorage.getItem(storageKey(shopId));
    return raw ? JSON.parse(raw) : [];
  } catch {
    return [];
  }
}

export function CartProvider({ children }) {
  const [shopId, setShopIdState] = useState(null);
  const [items, setItems] = useState([]);

  const setShopId = (id) => {
    if (id === shopId) return;
    setShopIdState(id);
    setItems(loadCart(id));
  };

  useEffect(() => {
    if (shopId) {
      localStorage.setItem(storageKey(shopId), JSON.stringify(items));
    }
  }, [items, shopId]);

  const addItem = (product, variant, quantity = 1) => {
    setItems((prev) => {
      const key = lineKey(product.id, variant?.id);
      const existing = prev.find(
        (i) => lineKey(i.productId, i.productVariantId) === key,
      );
      if (existing) {
        return prev.map((i) =>
          lineKey(i.productId, i.productVariantId) === key
            ? { ...i, quantity: i.quantity + quantity }
            : i,
        );
      }
      return [
        ...prev,
        {
          productId: product.id,
          productVariantId: variant?.id || null,
          title: product.title,
          image: product.image,
          price: variant?.price ?? product.price,
          variantName: variant?.name || null,
          quantity,
        },
      ];
    });
  };

  const updateQuantity = (productId, productVariantId, quantity) => {
    setItems((prev) => {
      if (quantity <= 0) {
        return prev.filter(
          (i) => lineKey(i.productId, i.productVariantId) !== lineKey(productId, productVariantId),
        );
      }
      return prev.map((i) =>
        lineKey(i.productId, i.productVariantId) === lineKey(productId, productVariantId)
          ? { ...i, quantity }
          : i,
      );
    });
  };

  const removeItem = (productId, productVariantId) =>
    updateQuantity(productId, productVariantId, 0);

  const clearCart = () => setItems([]);

  const itemCount = useMemo(
    () => items.reduce((sum, i) => sum + i.quantity, 0),
    [items],
  );

  const value = {
    shopId,
    setShopId,
    items,
    addItem,
    updateQuantity,
    removeItem,
    clearCart,
    itemCount,
  };

  return (
    <CartContext.Provider value={value}>{children}</CartContext.Provider>
  );
}

export function useCart() {
  const ctx = useContext(CartContext);
  if (!ctx) throw new Error("useCart must be used within a CartProvider");
  return ctx;
}
